// Snooze a task: mute it from the daily care-team digest and from the patient's automated
// reminders until a date. The due date is NOT changed — WF17 validates and clamps server-side
// (clinical alerts refused, billing clamped to their deadline, 30-day cap).
// Spec: docs/specs/2026-08-06-task-snooze.md

import { eventPost } from '@/lib/events';
import { currentUpdaterRole } from '@/config/program';
import { cleanActorOf, type CompletionActor } from '../endpoints';

export interface SnoozeOpts {
  until: string; // YYYY-MM-DD, inclusive
  reason: string; // required — the team sees it on the task
  withingsUserId: string | number;
  actor?: CompletionActor;
}

const who = (a?: CompletionActor) => [a?.firstname, a?.lastname].filter(Boolean).join(' ').trim() || 'care team';

export async function snoozeTask(
  taskId: string | number,
  {
    until, reason, withingsUserId, actor,
  }: SnoozeOpts,
): Promise<unknown> {
  const stamp = new Date().toISOString();
  const cleanActor = cleanActorOf(actor);
  return eventPost('task_updated', {
    task_id: taskId,
    id_dev_user: withingsUserId,
    updated_by_role: actor?.role ?? currentUpdaterRole(),
    changes: {
      // Its own change key, never raw context_json: WF17 owns validation + the merge, so the
      // caller can neither skip a guard rail nor clobber sequence / auto_complete_on / dos.
      snooze: { until, reason },
      completion_notes: `[Snoozed until ${until} by ${who(actor)} @ ${stamp}] ${reason}`,
    },
    // WF5 unpacks this to sent_by_* — which is the ONLY thing WF17 can read to fill
    // snooze.by. Omit it and the audit records nulls.
    ...(cleanActor ? { actor: cleanActor } : {}),
  });
}

export async function resumeTask(
  taskId: string | number,
  { withingsUserId, actor }: { withingsUserId: string | number; actor?: CompletionActor },
): Promise<unknown> {
  const stamp = new Date().toISOString();
  const cleanActor = cleanActorOf(actor);
  return eventPost('task_updated', {
    task_id: taskId,
    id_dev_user: withingsUserId,
    updated_by_role: actor?.role ?? currentUpdaterRole(),
    changes: {
      snooze: null,
      completion_notes: `[Resumed by ${who(actor)} @ ${stamp}]`,
    },
    ...(cleanActor ? { actor: cleanActor } : {}),
  });
}
